import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import api from '../../lib/api';
import { 
  Mail, Code, Eye, Save, RotateCcw, Search, 
  FileText, Sparkles, ChevronDown, ChevronUp, 
  Copy, Info, Send, Loader2
} from 'lucide-react';
import SimpleEmailEditor from './SimpleEmailEditor';

const EmailTemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState('visual'); // 'visual' or 'html'
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [expandedVariables, setExpandedVariables] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/email-templates');
      setTemplates(res.data);
      if (res.data.length > 0 && !selectedTemplate) {
        selectTemplate(res.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const selectTemplate = (template) => {
    setSelectedTemplate(template);
    setEditedSubject(template.subject);
    setEditedBody(template.body_html);
    setShowPreview(false);
    setEditMode('visual');
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      await api.put(`/email-templates/${selectedTemplate.template_key}`, {
        subject: editedSubject,
        body_html: editedBody
      });
      toast.success('Template saved successfully!');
      fetchTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedTemplate) return;
    
    if (!confirm('Reset this template to its default? This will undo all your changes.')) {
      return;
    }
    
    setSaving(true);
    try {
      const res = await api.post(`/email-templates/reset/${selectedTemplate.template_key}`);
      setEditedSubject(res.data.subject);
      setEditedBody(res.data.body_html);
      toast.success('Template reset to default');
      fetchTemplates();
    } catch (error) {
      console.error('Failed to reset template:', error);
      toast.error('Failed to reset template');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    const sampleData = {
      student_name: 'Emma Johnson',
      student_age: '8',
      parent_name: 'Sarah Johnson',
      guardian_name: 'Sarah Johnson',
      class_name: 'Primatotterina',
      class_list: '<li>Primatotterina</li><li>Bopper Ballet/Tap</li>',
      amount: '185.00',
      monthly_amount: '18.00',
      discount: '15.00',
      booking_id: 'ABC12345',
      trial_date: 'Saturday, 15th March 2026',
      course: 'Primatotterina',
      verification_link: 'https://upaschool.com/verify?token=xyz',
      enroll_url: 'https://upaschool.com/enroll',
      invite_link: 'https://upaschool.com/invite?code=abc',
      role: 'Teacher',
      invited_by: 'UPA Admin',
      sender_name: 'The UPA Team',
      subject: 'Important Update',
      content: '<p>This is sample content for the message preview.</p>',
      title: 'Term Dates Announcement',
      due_date: '1st April 2026',
      payment_link: 'https://upaschool.com/pay'
    };

    try {
      const res = await api.post('/email-templates/preview', {
        body_html: editedBody,
        variables: sampleData
      });
      setPreviewHtml(res.data.preview_html);
      setShowPreview(true);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to generate preview');
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingTest(true);
    try {
      await api.post('/email-templates/send-test', {
        template_key: selectedTemplate.template_key,
        to_email: testEmail,
        subject: editedSubject,
        body_html: editedBody
      });
      toast.success(`Test email sent to ${testEmail}!`);
      setTestEmail('');
    } catch (error) {
      console.error('Failed to send test email:', error);
      toast.error('Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const copyVariable = (variable) => {
    navigator.clipboard.writeText(`{{${variable}}}`);
    toast.success(`Copied {{${variable}}} to clipboard`);
  };

  const filteredTemplates = templates.filter(t => 
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const hasChanges = selectedTemplate && (
    editedSubject !== selectedTemplate.subject || 
    editedBody !== selectedTemplate.body_html
  );

  return (
    <div className="space-y-6" data-testid="email-templates-tab">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-heading text-xl font-semibold text-white">Email Templates</h2>
          <p className="text-sm text-white/50">Customize your automated emails - just type and format like a document</p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchTemplates}
          disabled={loading}
          className="border-[#333] text-white hover:bg-[#222]"
        >
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template List */}
        <Card className="bg-[#111] border-[#333] lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <Mail className="w-4 h-4 text-[#FF007F]" />
              Templates
            </CardTitle>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-[#0a0a0a] border-[#333] text-white text-sm"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0 max-h-[600px] overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-white/50">Loading templates...</div>
            ) : filteredTemplates.length === 0 ? (
              <div className="p-4 text-center text-white/50">No templates found</div>
            ) : (
              <div className="divide-y divide-[#222]">
                {filteredTemplates.map(template => (
                  <button
                    key={template.template_key}
                    onClick={() => selectTemplate(template)}
                    className={`w-full p-3 text-left transition-colors hover:bg-[#1a1a1a] ${
                      selectedTemplate?.template_key === template.template_key 
                        ? 'bg-[#1a1a1a] border-l-2 border-[#FF007F]' 
                        : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {template.name}
                        </p>
                        <p className="text-xs text-white/40 mt-0.5 line-clamp-2">
                          {template.description}
                        </p>
                      </div>
                      {template.updated_by && (
                        <Badge className="bg-blue-500/20 text-blue-300 text-[10px] shrink-0">
                          Modified
                        </Badge>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Editor */}
        <Card className="bg-[#111] border-[#333] lg:col-span-2">
          {selectedTemplate ? (
            <>
              <CardHeader className="border-b border-[#333] pb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-white text-lg">{selectedTemplate.name}</CardTitle>
                    <p className="text-sm text-white/50 mt-1">{selectedTemplate.description}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleReset}
                      disabled={saving}
                      className="border-[#333] text-white hover:bg-[#222]"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reset
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSave}
                      disabled={saving || !hasChanges}
                      className="bg-[#FF007F] hover:bg-[#FF007F]/80"
                    >
                      <Save className="w-4 h-4 mr-1" />
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </div>

                {/* Variables Section */}
                <div className="mt-4 bg-[#0a0a0a] rounded-lg border border-[#333]">
                  <button
                    onClick={() => setExpandedVariables(!expandedVariables)}
                    className="w-full p-3 flex items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-yellow-400" />
                      <span className="text-sm font-medium text-white">Available Variables (click to copy)</span>
                      <Badge className="bg-[#333] text-white/70 text-[10px]">
                        {selectedTemplate.variables.length}
                      </Badge>
                    </div>
                    {expandedVariables ? (
                      <ChevronUp className="w-4 h-4 text-white/50" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-white/50" />
                    )}
                  </button>
                  {expandedVariables && (
                    <div className="px-3 pb-3 flex flex-wrap gap-2">
                      {selectedTemplate.variables.map(variable => (
                        <button
                          key={variable}
                          onClick={() => copyVariable(variable)}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-[#1a1a1a] rounded text-xs text-yellow-400 hover:bg-[#222] transition-colors"
                        >
                          <code>{`{{${variable}}}`}</code>
                          <Copy className="w-3 h-3" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </CardHeader>

              <CardContent className="p-4 space-y-4">
                {/* Subject Line */}
                <div>
                  <Label className="text-white/70 text-sm">Subject Line</Label>
                  <Input
                    value={editedSubject}
                    onChange={(e) => setEditedSubject(e.target.value)}
                    className="mt-1 bg-[#0a0a0a] border-[#333] text-white"
                    placeholder="Email subject..."
                  />
                </div>

                {/* Editor Section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-white/70 text-sm">Email Content</Label>
                    <div className="flex gap-2">
                      <Button
                        variant={editMode === 'visual' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setEditMode('visual')}
                        className={editMode === 'visual' ? 'bg-[#FF007F]' : 'border-[#333] text-white hover:bg-[#222]'}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant={editMode === 'html' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setEditMode('html')}
                        className={editMode === 'html' ? 'bg-[#7000FF]' : 'border-[#333] text-white hover:bg-[#222]'}
                      >
                        <Code className="w-3 h-3 mr-1" />
                        HTML
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreview}
                        className="border-[#333] text-white hover:bg-[#222]"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Preview
                      </Button>
                    </div>
                  </div>

                  {editMode === 'visual' ? (
                    <SimpleEmailEditor
                      content={editedBody}
                      onChange={setEditedBody}
                    />
                  ) : (
                    <textarea
                      value={editedBody}
                      onChange={(e) => setEditedBody(e.target.value)}
                      className="w-full h-[400px] p-4 bg-[#0a0a0a] border border-[#333] rounded-lg text-white font-mono text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#FF007F]"
                      placeholder="HTML email content..."
                    />
                  )}
                </div>

                {/* Send Test Email */}
                <div className="bg-[#0a0a0a] rounded-lg border border-[#333] p-4">
                  <Label className="text-white/70 text-sm flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Send Test Email
                  </Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="flex-1 bg-[#111] border-[#333] text-white"
                    />
                    <Button
                      onClick={handleSendTestEmail}
                      disabled={sendingTest || !testEmail}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {sendingTest ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-1" />
                          Send
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-white/40 mt-2">
                    Test how your email looks in a real inbox (with sample data)
                  </p>
                </div>

                {/* Preview Modal */}
                {showPreview && (
                  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
                      <div className="flex items-center justify-between p-4 border-b bg-gray-50">
                        <div>
                          <h3 className="font-semibold text-gray-900">Email Preview</h3>
                          <p className="text-sm text-gray-500">Subject: {editedSubject}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowPreview(false)}
                        >
                          Close
                        </Button>
                      </div>
                      <div 
                        className="p-4 overflow-y-auto max-h-[70vh]"
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                      />
                    </div>
                  </div>
                )}

                {/* Last Updated Info */}
                {selectedTemplate.updated_by && (
                  <div className="flex items-center gap-2 text-xs text-white/40 pt-2 border-t border-[#222]">
                    <Info className="w-3 h-3" />
                    Last modified by {selectedTemplate.updated_by} on{' '}
                    {new Date(selectedTemplate.updated_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                )}
              </CardContent>
            </>
          ) : (
            <CardContent className="p-8 text-center">
              <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50">Select a template to edit</p>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
};

export default EmailTemplatesTab;
